import React from 'react';
import { Link } from 'react-router-dom';
import NavBar from '../../components/layout/NavBar';
import SiteFooter from '../../components/layout/SiteFooter';
import SEO from '../../components/SEO';
import Breadcrumbs from '../../components/Breadcrumbs';
import FAQSection from '../../components/FAQSection';
import { articleSchema, breadcrumbSchema, faqSchema } from '../../schema';

const BlogPostLayout = ({
  slug,
  category,
  title,
  description,
  datePublished,
  readTime,
  toc,
  faqItems,
  relatedLinks,
  references,
  children,
}) => {
  const path = `/blog/${slug}`;

  return (
    <div className="relative w-full min-h-screen">
      <SEO
        title={title}
        description={description}
        path={path}
        schema={[
          articleSchema({ title, description, path, datePublished }),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Blog', path: '/blog' }, { name: title, path }]),
          faqSchema(faqItems),
        ]}
      />
      <NavBar />
      <Breadcrumbs trail={[{ name: 'Home', path: '/' }, { name: 'Blog', path: '/blog' }, { name: title, path }]} />

      <article className="px-8 pb-4 pt-6 max-w-3xl mx-auto">
        <p className="text-cyan-400 font-mono text-[10px] tracking-[0.4em] uppercase mb-4">{category}</p>
        <h1 className="text-3xl md:text-5xl font-light tracking-tight text-white mb-6 leading-tight">{title}</h1>
        <div className="flex items-center gap-3 text-white/40 text-xs mb-2">
          <span className="text-white/70 font-medium">SaniQ Team</span>
          <span>·</span>
          <span>{datePublished}</span>
          <span>·</span>
          <span>{readTime}</span>
        </div>
        <p className="text-white/35 text-xs leading-relaxed mb-10 max-w-xl">
          Written by the SaniQ product &amp; hygiene science content team, covering entryway hygiene research and the engineering behind SaniQ.
        </p>

        {toc && toc.length > 0 && (
          <nav aria-label="Table of contents" className="glass rounded-2xl border border-white/10 p-6 mb-12">
            <p className="text-white text-xs uppercase tracking-widest font-bold mb-4">Table of Contents</p>
            <ol className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {toc.map((item) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-white/50 text-xs hover:text-cyan-400 transition-colors">
                    {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <div className="blog-body space-y-10 text-white/60 text-sm md:text-base leading-relaxed">
          {children}
        </div>
      </article>

      {faqItems && faqItems.length > 0 && <FAQSection heading="Frequently Asked Questions" items={faqItems} />}

      {relatedLinks && relatedLinks.length > 0 && (
        <section className="py-12 px-8 max-w-3xl mx-auto border-t border-white/10">
          <h2 className="text-2xl font-light text-white mb-8">Related Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {relatedLinks.map((link) => (
              <Link key={link.to} to={link.to} className="glass rounded-2xl p-5 border border-white/10 hover:border-cyan-500/30 transition-colors">
                <p className="text-cyan-400 text-[10px] uppercase tracking-widest font-bold mb-2">{link.type}</p>
                <p className="text-white text-sm">{link.label}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {references && references.length > 0 && (
        <section className="py-12 px-8 max-w-3xl mx-auto border-t border-white/10">
          <h2 className="text-2xl font-light text-white mb-6">References</h2>
          <ul className="space-y-3">
            {references.map((ref) => (
              <li key={ref.url} className="text-white/45 text-xs leading-relaxed">
                <a href={ref.url} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 transition-colors break-words">
                  {ref.label}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      <SiteFooter />
    </div>
  );
};

export default BlogPostLayout;
